import { useState, memo, useCallback } from 'react';
import { ChevronDown, MapPin, Heart, Send, User, Briefcase, Award, Shield, Home, Wifi, Users as UsersIcon, Clock, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AwardsDisplay } from '@/components/AwardsDisplay';
import {
  getCivilianizedTitle,
  getServiceContext,
  getFormattedRank,
  calculateYearsOfService,
  getCoreStrengths,
} from '@/utils/militaryTranslation';

/** Chip config for candidate signal pills */
const SIGNAL_CHIP = {
  near_you:       { label: 'Near You',        icon: Home,   style: 'bg-sky-50 text-sky-700 border-sky-200' },
  within_region:  { label: 'Within Region',   icon: MapPin, style: 'bg-teal-50 text-teal-700 border-teal-200' },
  available_now:  { label: 'Available now',    icon: Clock,  style: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  leadership:     { label: 'Leadership',       icon: UsersIcon, style: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  skills_match:   { label: 'Skills Match',     icon: Target, style: 'bg-brand-light text-brand border-brand/20' },
  clearance:      { label: 'Security Clearance', icon: Shield, style: 'bg-amber-50 text-amber-700 border-amber-200' },
};

// Qualification categories (for the compact card qualification chips)
const QUAL_CATS = new Set(['badge', 'tab', 'qualification', 'warfare_qualification', 'military_school', 'specialty_qualification']);

// Compact clearance label for the summary card (candidate-reported)
function clearanceCompact(level, sci) {
  if (level === 'ts_sci' || (level === 'top_secret' && sci)) return 'TS/SCI';
  const m = { confidential: 'Confidential', secret: 'Secret', top_secret: 'Top Secret', doe_l: 'DOE L', doe_q: 'DOE Q', q_clearance: 'DOE Q', l_clearance: 'DOE L', other: 'Clearance' };
  return m[level] || 'Clearance';
}
// Fuller clearance label for the expanded profile
function clearanceFull(level, sci) {
  if (level === 'ts_sci' || (level === 'top_secret' && sci)) return 'Top Secret with SCI';
  const m = { confidential: 'Confidential', secret: 'Secret', top_secret: 'Top Secret', doe_l: 'DOE L', doe_q: 'DOE Q', q_clearance: 'DOE Q', l_clearance: 'DOE L', other: 'Other' };
  return m[level] || level;
}
// Self-reported access status mapping (only the values we collect)
const ACCESS_LABEL = {
  current_access: 'Currently using this clearance',
  previously_held: 'Held previously, not currently using',
  unknown: 'Not sure',
};

/**
 * Individual Candidate Accordion Item
 * Optimized for high-volume scanning with progressive disclosure
 */
const CandidateAccordionItem = memo(function CandidateAccordionItem({
  candidate,
  isExpanded,
  onToggle,
  isFavorite,
  onToggleFavorite,
  onInvite,
  onViewProfile,
  hasActiveJobs = true,
  appInfo = null,
  rankJobId = null,
  onOpenApplication,
}) {
  const personalInfo = candidate.personal_info || {};
  const militaryRoles = candidate.military_roles || [];
  const mostRecentRole = militaryRoles[0] || null;
  
  // Derived display values
  const civilianTitle = getCivilianizedTitle(mostRecentRole);
  const serviceContext = getServiceContext(mostRecentRole, mostRecentRole?.branch);
  const formattedRank = getFormattedRank(mostRecentRole?.rank);
  const yearsOfService = calculateYearsOfService(militaryRoles);
  const coreStrengths = getCoreStrengths(militaryRoles, 5);
  const location = [personalInfo.city, personalInfo.state].filter(Boolean).join(', ');

  // ── Application relationship (canonical) ──
  const apps = appInfo?.applications || [];
  const relevantApp = apps.find(a => a.job_id === rankJobId) || apps[0] || null;
  const hasMultipleApps = apps.length > 1;
  const appBadge = relevantApp
    ? (hasMultipleApps ? `${apps.length} applications` : `Applied • ${relevantApp.stage_label}`)
    : null;
  const [showAppChooser, setShowAppChooser] = useState(false);

  const chooseApp = (app) => { setShowAppChooser(false); onOpenApplication?.(candidate, app); };

  // Build signal chips from backend-computed fields
  const signalChips = [];
  // Proximity
  if (candidate.proximity_label === 'Near You') signalChips.push({ key: 'near_you', ...SIGNAL_CHIP.near_you });
  else if (candidate.proximity_label === 'Within Region') signalChips.push({ key: 'within_region', ...SIGNAL_CHIP.within_region });
  // Leadership (from explanation tags or rank)
  const tags = candidate.explanation?.tags || [];
  if (tags.includes('leadership_signal')) signalChips.push({ key: 'leadership', ...SIGNAL_CHIP.leadership });
  if (tags.includes('skills_overlap_high')) signalChips.push({ key: 'skills_match', ...SIGNAL_CHIP.skills_match });
  // Base label
  if (candidate.base_label) signalChips.push({ key: 'base', label: candidate.base_label, icon: MapPin, style: 'bg-teal-50 text-teal-700 border-teal-200' });
  // Availability
  if (candidate.availability_label === 'Available Now') signalChips.push({ key: 'available_now', ...SIGNAL_CHIP.available_now });  else if (candidate.availability_label) signalChips.push({ key: 'availability', label: candidate.availability_label, icon: Clock, style: 'bg-slate-50 text-slate-600 border-slate-200' });
  // Clearance (candidate-reported) — show the normalized level compactly
  if (candidate.security_clearance && !['none', ''].includes(candidate.security_clearance)) {
    signalChips.push({ key: 'clearance', ...SIGNAL_CHIP.clearance, label: clearanceCompact(candidate.security_clearance, candidate.security_clearance_sci) });
  }

  // Compact qualification chips (qualification categories only)
  const qualChips = (candidate.structured_awards || [])
    .filter(sa => QUAL_CATS.has(sa.category))
    .map(sa => sa.display_name || sa.award_id);
  
  // Handle click on the entire row
  const handleRowClick = useCallback((e) => {
    // Don't toggle if clicking on buttons
    if (e.target.closest('button')) return;
    onToggle();
  }, [onToggle]);
  
  // Handle favorite click without propagation
  const handleFavoriteClick = useCallback((e) => {
    e.stopPropagation();
    onToggleFavorite(candidate.user_id);
  }, [onToggleFavorite, candidate.user_id]);
  
  return (
    <div 
      className={cn(
        "bg-white border rounded-lg transition-all duration-200",
        isExpanded ? "border-brand shadow-md" : "border-slate-200 hover:border-slate-300"
      )}
      data-testid={`candidate-accordion-${candidate.user_id}`}
    >
      {/* CLOSED STATE - The "Tease" */}
      <div
        className="flex items-start justify-between p-4 cursor-pointer select-none"
        onClick={handleRowClick}
        data-testid={`candidate-row-${candidate.user_id}`}
      >
        <div className="flex items-start gap-4 flex-1 min-w-0">
          {/* Score badge (when scored) */}
          {candidate.final_ranked_score != null && (
            <div className="hidden sm:flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-slate-50 flex-shrink-0">
              <span className={`text-base font-bold tabular-nums ${
                candidate.final_ranked_score >= 70 ? 'text-brand' :
                candidate.final_ranked_score >= 40 ? 'text-amber-600' : 'text-slate-400'
              }`}>{Math.round(candidate.final_ranked_score)}</span>
              <span className="text-[8px] font-semibold text-slate-400 uppercase tracking-wider -mt-0.5">match</span>
            </div>
          )}
          
          {/* Main info */}
          <div className="min-w-0 flex-1">
            {/* TOP ROW: Name + Civilianized Role + Location (metadata only, NO pills) */}
            <h3 className="text-base font-semibold text-slate-900 truncate">
              {personalInfo.full_name || 'Anonymous Candidate'}
            </h3>
            {appBadge && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasMultipleApps) {
                    setShowAppChooser(true);
                    if (!isExpanded) onToggle();
                  } else {
                    onOpenApplication?.(candidate, relevantApp);
                  }
                }}
                className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 transition-colors"
                data-testid={`application-badge-${candidate.user_id}`}
              >
                <Briefcase className="w-3 h-3" />
                {appBadge}
              </button>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-sm text-slate-500 mt-0.5">
              <span className="font-medium text-brand truncate">{civilianTitle}</span>
              {location && (
                <span className="flex items-center gap-1 text-xs">
                  <MapPin className="w-3 h-3" />
                  {location}
                </span>
              )}
              {(candidate.distance_home_miles != null || candidate.distance_base_miles != null) ? (
                <span className="flex items-center gap-x-2 gap-y-0 flex-wrap text-[11px] text-slate-400" data-testid="candidate-distance">
                  {candidate.distance_home_miles != null && (
                    <span data-testid="distance-from-home">{Math.round(candidate.distance_home_miles)} mi from home</span>
                  )}
                  {candidate.distance_base_miles != null && (
                    <span data-testid="distance-from-duty">
                      {Math.round(candidate.distance_base_miles)} mi from {candidate.duty_station_name || 'duty station'}
                    </span>
                  )}
                </span>
              ) : candidate.distance_miles != null ? (
                <span className="text-[11px] text-slate-400">{Math.round(candidate.distance_miles)} mi away</span>
              ) : (
                <span className="text-[11px] text-slate-400 italic">Distance not shared</span>
              )}
            </div>

            {/* SECOND ROW: Signal chips (pills) */}
            {signalChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {signalChips.slice(0, 5).map((chip) => {
                  const Icon = chip.icon;
                  return (
                    <span key={chip.key} className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${chip.style}`}>
                      <Icon className="w-3 h-3" />
                      {chip.label}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Compact military qualifications (qualification categories only) */}
            {qualChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`card-qualifications-${candidate.user_id}`}>
                {qualChips.slice(0, 3).map((n, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full border bg-teal-50 text-teal-700 border-teal-200 text-[11px] font-medium">
                    {n}
                  </span>
                ))}
                {qualChips.length > 3 && (
                  <span className="text-[11px] text-slate-500 font-medium">+{qualChips.length - 3}</span>
                )}
              </div>
            )}

            {/* SUMMARY LINE */}
            {candidate.summary_line && (
              <p className="text-xs text-slate-500 mt-1.5 line-clamp-1">{candidate.summary_line}</p>
            )}
          </div>
        </div>
        
        {/* Right side actions */}
        <div className="flex items-center gap-2 ml-4 flex-shrink-0 mt-1">
          {/* Favorite button */}
          <button
            onClick={handleFavoriteClick}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid={`favorite-btn-${candidate.user_id}`}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart 
              className={cn(
                "w-5 h-5 transition-colors",
                isFavorite ? "fill-red-500 text-red-500" : "text-slate-400 hover:text-slate-600"
              )} 
            />
          </button>
          
          {/* Expand indicator */}
          <ChevronDown 
            className={cn(
              "w-5 h-5 text-slate-400 transition-transform duration-200",
              isExpanded && "rotate-180"
            )} 
          />
        </div>
      </div>
      
      {/* EXPANDED STATE - Context, Not Resume */}
      {isExpanded && (
        <div 
          className="px-4 pb-4 pt-0 border-t border-slate-100"
          data-testid={`candidate-expanded-${candidate.user_id}`}
        >
          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Most Recent Military Role (translated) */}
              {mostRecentRole && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5" />
                    Most Recent Role
                  </h4>
                  <p className="text-sm font-medium text-slate-900">
                    {civilianTitle}
                  </p>
                  {mostRecentRole.ai_summary_text && (
                    <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                      {mostRecentRole.ai_summary_text}
                    </p>
                  )}
                </div>
              )}
              
              {/* Service Context */}
              {mostRecentRole && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    Service Background
                  </h4>
                  <div className="space-y-1">
                    {mostRecentRole.branch && (
                      <p className="text-sm text-slate-700">
                        <span className="text-slate-500">Branch:</span> U.S. {mostRecentRole.branch}
                      </p>
                    )}
                    {formattedRank && (
                      <p className="text-sm text-slate-700">
                        <span className="text-slate-500">Rank:</span> {formattedRank}
                      </p>
                    )}
                    {yearsOfService && (
                      <p className="text-xs text-slate-500">
                        {yearsOfService} year{yearsOfService !== 1 ? 's' : ''} of service
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Right Column */}
            <div className="space-y-4">
              {/* Core Strengths */}
              {coreStrengths.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5" />
                    Core Strengths
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {coreStrengths.map((skill, idx) => (
                      <span 
                        key={idx} 
                        className="px-2 py-0.5 bg-brand-light text-brand rounded text-xs font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Location */}
              {location && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    Location
                  </h4>
                  <p className="text-sm text-slate-700">{location}</p>
                </div>
              )}

              {/* Security Clearance (candidate-reported) */}
              {candidate.security_clearance && !['none', ''].includes(candidate.security_clearance) && (
                <div data-testid={`expanded-clearance-${candidate.user_id}`}>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" />
                    Security Clearance
                  </h4>
                  <p className="text-sm font-medium text-slate-900">
                    {clearanceFull(candidate.security_clearance, candidate.security_clearance_sci)}
                  </p>
                  {ACCESS_LABEL[candidate.security_clearance_access_status] && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Current access: {ACCESS_LABEL[candidate.security_clearance_access_status]}
                    </p>
                  )}
                  <p className="text-[10px] text-slate-400 mt-0.5">Self-reported</p>
                </div>
              )}

              {/* Military qualifications & awards (split view) */}
              <AwardsDisplay resume={candidate} />
            </div>
          </div>
          
          {/* Primary CTAs - SEPARATED ACTIONS */}
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-slate-100">
            {/* Open Application — direct when one application; a chooser when several */}
            {relevantApp && onOpenApplication && (
              hasMultipleApps ? (
                <div className="relative">
                  <Button
                    onClick={() => setShowAppChooser(v => !v)}
                    size="sm"
                    className="bg-brand hover:bg-brand-hover text-white"
                    data-testid={`view-applications-btn-${candidate.user_id}`}
                  >
                    <Briefcase className="w-4 h-4 mr-1.5" />
                    View {apps.length} Applications
                  </Button>
                  {showAppChooser && (
                    <div className="absolute z-20 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-lg p-1.5" data-testid={`app-chooser-${candidate.user_id}`}>
                      {apps.map((a) => (
                        <button
                          key={a.application_id}
                          type="button"
                          onClick={() => chooseApp(a)}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                          data-testid={`app-chooser-item-${a.application_id}`}
                        >
                          <p className="text-sm font-medium text-slate-900 truncate">{a.job_title}</p>
                          <p className="text-xs text-emerald-700">{a.stage_label}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  onClick={() => onOpenApplication(candidate, relevantApp)}
                  size="sm"
                  className="bg-brand hover:bg-brand-hover text-white"
                  data-testid={`open-application-btn-${candidate.user_id}`}
                >
                  <Briefcase className="w-4 h-4 mr-1.5" />
                  Open Application
                </Button>
              )
            )}
            {/* View Profile = Message/Connect (NO job selection) */}
            {onViewProfile && (
              <Button
                onClick={() => onViewProfile(candidate)}
                variant="outline"
                size="sm"
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid={`view-profile-btn-${candidate.user_id}`}
              >
                <User className="w-4 h-4 mr-1.5" />
                Open profile
              </Button>
            )}
            
            {/* Invite to Apply = Job-specific invite (REQUIRES job selection) */}
            <Button
              onClick={() => onInvite(candidate)}
              size="sm"
              disabled={!hasActiveJobs}
              title={!hasActiveJobs ? 'You need an active job posting to invite candidates' : undefined}
              className={cn(
                "text-white",
                hasActiveJobs 
                  ? "bg-brand hover:bg-brand-hover" 
                  : "bg-slate-300 cursor-not-allowed"
              )}
              data-testid={`invite-btn-${candidate.user_id}`}
            >
              <Send className="w-4 h-4 mr-1.5" />
              Reach out
            </Button>
            
            {/* Tooltip for disabled state */}
            {!hasActiveJobs && (
              <span className="text-xs text-slate-500 self-center">
                Post a job to invite candidates
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * Candidate Accordion List
 * Main component for browsing candidates with progressive disclosure
 */
export function CandidateAccordionList({
  candidates,
  favorites = new Set(),
  onToggleFavorite,
  onInvite,
  onViewProfile,
  hasActiveJobs = true,
  singleExpand = true, // Only one accordion open at a time
  appContext = {},
  rankJobId = null,
  onOpenApplication,
}) {
  const [expandedId, setExpandedId] = useState(null);
  
  const handleToggle = useCallback((candidateId) => {
    setExpandedId(prev => prev === candidateId ? null : candidateId);
  }, []);
  
  if (!candidates || candidates.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
        <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-600 font-medium">No candidates found</p>
        <p className="text-sm text-slate-500 mt-1">Try adjusting your search filters</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2" data-testid="candidate-accordion-list">
      {/* Result count */}
      <p className="text-sm text-slate-500 mb-3">
        {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} found
      </p>
      
      {candidates.map((candidate) => (
        <CandidateAccordionItem
          key={candidate.user_id}
          candidate={candidate}
          isExpanded={expandedId === candidate.user_id}
          onToggle={() => handleToggle(candidate.user_id)}
          isFavorite={favorites.has(candidate.user_id)}
          onToggleFavorite={onToggleFavorite}
          onInvite={onInvite}
          onViewProfile={onViewProfile}
          hasActiveJobs={hasActiveJobs}
          appInfo={appContext[candidate.user_id] || null}
          rankJobId={rankJobId}
          onOpenApplication={onOpenApplication}
        />
      ))}
    </div>
  );
}

export default CandidateAccordionList;
